'use client';

import Link from 'next/link';
import { useState } from 'react';
import classNames from 'classnames';
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  LinkList,
  LinkListItem,
} from 'design-react-kit';
import type { ResolvedHeaderNavItem } from '@/helpers/headerNavigation.helper';

type HeaderMainNavigationClientProps = {
  items: ResolvedHeaderNavItem[];
};

function NavLinkItem({ item }: { item: ResolvedHeaderNavItem }) {
  if (!item.href) return null;

  return (
    <Link
      prefetch={false}
      href={item.href}
      className="text-white"
      target={item.openInNewTab ? '_blank' : undefined}
      rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
      aria-label={item.ariaLabel || undefined}
    >
      {item.label}
    </Link>
  );
}

function NavItem({
  item,
  pushRight,
}: {
  item: ResolvedHeaderNavItem;
  pushRight?: boolean;
}) {
  const isDropdown = item.type === 'Dropdown';
  const [open, setOpen] = useState(false);

  if (!isDropdown && !item.href) return null;

  if (isDropdown) {
    return (
      <li
        className={classNames(
          'it-header-nav-dropdown',
          open && 'show',
          pushRight && 'ms-auto',
        )}
      >
        <Dropdown isOpen={open} toggle={() => setOpen((prev) => !prev)}>
          <DropdownToggle
            caret
            tag="a"
            className="text-white it-header-nav-dropdown__toggle"
          >
            <span>{item.label}</span>
          </DropdownToggle>
          <DropdownMenu>
            <LinkList>
              {(item.children ?? []).map((child) => (
                <LinkListItem
                  key={`${child.href}-${child.label}`}
                  inDropdown
                  href={child.href}
                  target={child.openInNewTab ? '_blank' : undefined}
                  rel={child.openInNewTab ? 'noopener noreferrer' : undefined}
                  onClick={() => setOpen(false)}
                >
                  <span>{child.label}</span>
                </LinkListItem>
              ))}
            </LinkList>
          </DropdownMenu>
        </Dropdown>
      </li>
    );
  }

  return (
    <li className={classNames(pushRight && 'ms-auto')}>
      <NavLinkItem item={item} />
    </li>
  );
}

export default function HeaderMainNavigationClient({
  items,
}: HeaderMainNavigationClientProps) {
  if (!items.length) return null;

  const leftItems = items.filter((item) => item.position !== 'Right');
  const rightItems = items.filter((item) => item.position === 'Right');

  return (
    <div className="it-header-slim-wrapper d-none d-lg-block teal-bg no-padding-tablet h-auto">
      <div className="container-xxl">
        <div className="row h-full">
          <div className="col-12">
            <nav
              className="it-header-middle-menu-wrapper"
              aria-label="Menu principale"
            >
              <ul className="d-flex align-items-center w-100">
                {leftItems.map((item) => (
                  <NavItem key={item.key} item={item} />
                ))}
                {rightItems.map((item, index) => (
                  <NavItem
                    key={item.key}
                    item={item}
                    pushRight={index === 0}
                  />
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
